import { onMounted, onUnmounted, nextTick, ref, watch, type Ref } from 'vue';

/**
 * Minimum font size (px) the button label will ever shrink to.
 * Below this the text becomes hard to read — fall back to ellipsis instead.
 */
const MIN_FONT_PX = 12;

/** Horizontal padding never collapses below this (px) when scaled down. */
const MIN_PADDING_PX = 8;

/** Icon-to-label gap never collapses below this (px) when scaled down. */
const MIN_GAP_PX = 4;

/**
 * Resolve the underlying HTMLElement from a ref that may hold either a DOM
 * element directly (native tag) or a Vue component proxy (router-link).
 */
function resolveEl(ref: Ref<unknown>): HTMLElement | null {
  const v = ref.value;
  if (!v) return null;
  if (v instanceof HTMLElement) return v;
  // Component proxy — pull the root DOM node
  const proxy = v as { $el?: unknown };
  return proxy.$el instanceof HTMLElement ? proxy.$el : null;
}

function clearFitStyles(el: HTMLElement, labelEl: HTMLElement | null): void {
  el.style.fontSize = '';
  el.style.paddingInline = '';
  el.style.gap = '';
  el.style.removeProperty('--m3-btn-icon-px');
  if (labelEl) {
    labelEl.style.overflow = '';
    labelEl.style.textOverflow = '';
    labelEl.style.maxWidth = '';
  }
}

export interface ButtonFitResult {
  /**
   * True when the label has been truncated to ellipsis (the MIN_FONT_PX floor
   * was still not enough). Useful for auto-echoing the full label in a tooltip.
   */
  isEllipsized: Ref<boolean>;
}

/**
 * useButtonFit — auto-scales a button's font, horizontal padding, icon, and
 * icon-to-label gap so the label always fits on one line without clipping.
 *
 * Runs after mount, whenever `label`/`size` change, and whenever the button
 * is resized by its container (ResizeObserver).
 *
 * Flow (per fit cycle, entirely within one frame — no intermediate paint):
 *
 * 1. Wait for nextTick so the DOM reflects the latest label (the watch case
 *    fires before Vue patches the DOM).
 * 2. Icon-only button (no `.m3-btn__label`)? Clear any stale overrides, done.
 * 3. Reset inline font/padding/gap overrides + icon CSS variable, measure:
 *    fits → done. This is the common case and touches visibility not at all.
 * 4. Overflow → hide the button (belt-and-braces against flash), then binary
 *    search the largest integer font size in [MIN_FONT_PX, design size] at
 *    which the label fits; scale padding-inline, gap, and icon font-size
 *    proportionally (floors: 8px padding, 4px gap, 12px icon).
 *    The font only ever shrinks below the design size — it is never enlarged
 *    past the size tier's CSS value.
 * 5. Still overflowing at MIN_FONT_PX → text-overflow: ellipsis on the label
 *    span as the last-resort fallback (full text stays available via the
 *    `tooltip` prop where the call site provides one, or auto-echoed via the
 *    `isEllipsized` return value).
 * 6. Restore visibility.
 *
 * Note: slot-driven label changes don't trigger the watch, but any change
 * that alters the rendered size is still caught by the ResizeObserver.
 *
 * Icon sizing contract: icons inside `.m3-btn__icon` must NOT have a Quasar
 * `:size` prop — icon font-size is controlled by the `--m3-btn-icon-px` CSS
 * variable set on the button element, with per-size-tier fallbacks in the
 * BaseButton SCSS. This lets the composable scale the icon in the same frame
 * as the font/padding without fighting Quasar inline styles.
 */
export function useButtonFit(
  btnRef: Ref<unknown>,
  deps: { label: Ref<string | undefined>; size: Ref<string> },
): ButtonFitResult {
  let measuring = false;
  let ro: ResizeObserver | null = null;
  const isEllipsized = ref(false);

  const fit = (): void => {
    // Guard re-entry (ResizeObserver can fire for our own DOM mutations).
    if (measuring) return;
    if (!resolveEl(btnRef)) return;

    measuring = true;

    void nextTick(() => {
      const el = resolveEl(btnRef);
      if (!el) {
        measuring = false;
        return;
      }

      const labelEl = el.querySelector<HTMLElement>('.m3-btn__label');

      // Icon-only buttons have nothing to fit.
      if (!labelEl) {
        clearFitStyles(el, null);
        isEllipsized.value = false;
        measuring = false;
        return;
      }

      // Reset overrides so the size tier's CSS values are back in force,
      // then measure. Reads force a synchronous reflow — still pre-paint.
      clearFitStyles(el, labelEl);
      isEllipsized.value = false;

      // Fast path (the overwhelmingly common case): design size fits.
      if (el.scrollWidth <= el.clientWidth) {
        measuring = false;
        return;
      }

      // ── Overflow: hide while we mutate, binary-search the best fit ────────
      el.style.visibility = 'hidden';

      const cs = getComputedStyle(el);
      const maxFont = parseFloat(cs.fontSize);
      const basePadding = parseFloat(cs.paddingLeft); // symmetric padding
      const baseGap = parseFloat(cs.columnGap) || 8;

      // Icon base size comes from the CSS-variable fallback (the per-size-tier
      // rule in BaseButton SCSS), readable now that --m3-btn-icon-px is unset.
      const iconEl = el.querySelector<HTMLElement>('.m3-btn__icon');
      const baseIconPx = iconEl ? parseFloat(getComputedStyle(iconEl).fontSize) : 0;

      const apply = (px: number): void => {
        const ratio = px / maxFont;
        el.style.fontSize = `${px}px`;
        el.style.paddingInline = `${Math.max(MIN_PADDING_PX, Math.round(basePadding * ratio))}px`;
        el.style.gap = `${Math.max(MIN_GAP_PX, Math.round(baseGap * ratio))}px`;
        if (baseIconPx > 0) {
          el.style.setProperty('--m3-btn-icon-px', `${Math.max(12, Math.round(baseIconPx * ratio))}px`);
        }
      };

      let lo = MIN_FONT_PX;
      let hi = Math.floor(maxFont);
      let best = MIN_FONT_PX;

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        apply(mid);
        if (el.scrollWidth <= el.clientWidth) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      apply(best);

      // ── Ellipsis fallback if even MIN_FONT_PX overflows ───────────────────
      if (el.scrollWidth > el.clientWidth) {
        labelEl.style.overflow = 'hidden';
        labelEl.style.textOverflow = 'ellipsis';
        labelEl.style.maxWidth = '100%';
        isEllipsized.value = true;
      }

      el.style.visibility = '';
      measuring = false;
    });
  };

  onMounted(() => {
    void nextTick(() => {
      fit();
      const el = resolveEl(btnRef);
      if (el) {
        ro = new ResizeObserver(fit);
        ro.observe(el);
      }
    });
  });

  onUnmounted(() => {
    ro?.disconnect();
    ro = null;
  });

  // Re-fit whenever the label text or size prop changes.
  watch([() => deps.label.value, () => deps.size.value], fit);

  return { isEllipsized };
}
